<script lang="ts">
import { ApplePay, DataCollector, FetchPaymentMethodsPayload } from 'braintree-web';
import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useCheckout } from '@/composables/useCheckout';
import { applePayPaymentItem, usePayment } from '@/composables/usePayment';
import money from '@/filters/money';
import { NutsLineItem } from '@/lib/cart/lineItem';
import { hasNonExpiredDefaultPaymentMethod } from '@/lib/checkout/payment';
import { NutsAddress } from '@/utils/address';
import { CheckoutEvents, formatPurchaseItem, gtag } from '@/utils/analytics';
import { generateRecaptcha } from '@/utils/captcha';
import { reportError } from '@/utils/reportError';

export default defineComponent({
  name: 'ApplePayButton',
  props: {
    containsAutoDelivery: { type: Boolean, default: false },
    createAccountPassword: { type: String },
    disabled: { type: Boolean },
    isB2b: { type: Boolean, default: false },
    savedPaymentMethods: { type: Array as PropType<FetchPaymentMethodsPayload[]> },
  },
  setup(props) {
    const router = useRouter();
    const store = useStore();

    const { placeOrder } = useCheckout(store);
    const { estimatedAmountToCharge, paymentItems } = usePayment(store, ref(false));

    const applePay = ref<ApplePay>();
    const braintreeDataCollector = ref<DataCollector>();
    const merchantName = 'Nuts.com';
    const session = ref<ApplePaySession>();

    const paymentLineItems = computed(() =>
      paymentItems.value
        .filter((p) => p.amount !== undefined)
        .map((p) => applePayPaymentItem(p, 'final')),
    );

    const total = computed(() =>
      applePayPaymentItem({
        amount: estimatedAmountToCharge.value,
        label: merchantName,
      }),
    );

    onMounted(async () => {
      const { useBraintree } = await import('@/stores/braintree');
      const braintreeStore = useBraintree();

      const applePayInstance = await braintreeStore.initApplePay();
      // @ts-ignore (@types/braintree-web is wrong and attribute does exist)
      const { merchantIdentifier } = applePayInstance;
      // eslint-disable-next-line no-undef -- using `window` loses typing for some reason
      if (!(await ApplePaySession?.canMakePaymentsWithActiveCard(merchantIdentifier))) {
        console.error('Apple Pay unable to finish initialization');
        return;
      }
      applePay.value = applePayInstance;
      braintreeDataCollector.value = await braintreeStore.initDataCollector();
    });

    const requestApplePayPayment = () => {
      if (!applePay.value) throw Error('Missing required Apple Pay instance!');

      type BraintreeApplePayConfig = Pick<
        ApplePayJS.ApplePayPaymentRequest,
        'merchantCapabilities' | 'supportedNetworks'
      >;
      // @ts-ignore (@types/braintree-web is wrong and exposes a Partial<ApplePayPaymentRequest>)
      const braintreeConfig: BraintreeApplePayConfig = applePay.value.createPaymentRequest({});
      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        ...braintreeConfig,
        countryCode: 'US',
        currencyCode: 'USD',
        lineItems: paymentLineItems.value,
        requiredBillingContactFields: ['postalAddress', 'phone', 'email', 'name'],
        requiredShippingContactFields: [],
        total: total.value,
      };
      session.value = new ApplePaySession(3, paymentRequest);

      session.value.onvalidatemerchant = async (event) => {
        try {
          // @ts-ignore (@types/braintree-web is wrong and works with promises)
          const merchantSession = await applePay.value.performValidation({
            validationURL: event.validationURL,
            displayName: merchantName,
          });
          session.value?.completeMerchantValidation(merchantSession);
        } catch (err) {
          reportError(err, 'Apple Pay failed to load');
          // eslint-disable-next-line no-alert
          alert('Apple Pay failed to load');
        }
      };

      // Payment Authorized
      session.value.onpaymentauthorized = async (event) => {
        let nonce: string | undefined;
        try {
          // @ts-ignore (@types/braintree-web is wrong; now takes an object and handle promises)
          ({ nonce } = await applePay.value.tokenize({ token: event.payment.token }));
        } catch (err) {
          const label = reportError(err, 'Apple Pay failed to tokenize');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.value?.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          return;
        }
        const maybeAddress = (contactAddress?: ApplePayJS.ApplePayPaymentContact) => {
          if (!contactAddress) return undefined;
          return NutsAddress.fromApplePay(contactAddress);
        };

        const saveToWallet = props.containsAutoDelivery;
        const setAsDefaultInWallet =
          saveToWallet && !hasNonExpiredDefaultPaymentMethod(props.savedPaymentMethods || []);

        try {
          const captchaResponse = await generateRecaptcha();
          const { order, receiptToken } = await placeOrder({
            billingAddress: {
              ...NutsAddress.fromApplePay(event.payment.billingContact ?? {}),
              email: maybeAddress(event.payment.shippingContact)?.email,
            },
            deviceData: braintreeDataCollector.value?.deviceData,
            'g-recaptcha-response': captchaResponse,
            isB2b: props.isB2b,
            newPassword: props.createAccountPassword,
            saveToWallet,
            setAsDefaultInWallet,
            paymentNonces: [`apple_pay_card:${nonce}`],
          });

          gtag('event', 'add_payment_info', {
            checkout_type: 'Apple Pay',
            coupon: order.discountCodes?.[0]?.discountCode.obj?.name?.en,
            currency: 'USD',
            items: order.lineItems.map((li) => formatPurchaseItem(NutsLineItem.fromCt(li))),
            payment_type: order.paymentInfo?.payments[0]?.obj?.paymentMethodInfo?.name?.en,
            value: money(estimatedAmountToCharge.value),
          });

          CheckoutEvents.purchase({
            order,
            paymentItems: paymentItems.value,
            paymentOption: 'ApplePay',
            receiptToken,
          });

          session.value?.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
          window.scrollTo({ top: 0, behavior: 'smooth' });
          router.push({ name: 'CheckoutReceipt', query: { token: receiptToken } });
        } catch (err) {
          const label = reportError(err, 'Apple Pay failed to finalize order');
          CheckoutEvents.event({ action: 'Express Checkout', label, category: 'Checkout Error' });
          session.value?.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          session.value?.abort();
          // eslint-disable-next-line no-alert
          alert('Apple Pay failed to finalize order');
        }
      };

      session.value?.begin();
    };

    return {
      applePay,
      requestApplePayPayment,
    };
  },
});
</script>

<template>
  <button
    v-if="applePay"
    class="w-full button apple-pay"
    data-test="apple-pay"
    :disabled
    @click.prevent="requestApplePayPayment"
  >
    Apple Pay
  </button>
</template>
